<template>
  <div class="btn-wrapper" ref="buttonWrapperRef">
    <button
      class="ui-button"
      :class="buttonClasses"
      :style="{ color: color || $colors.gray20 }"
      @click="onClick"
      type="button"
      :disabled="disabled"
    >
      <div class="top-icons">
        <ui-tooltip-icon
          v-if="helpTooltip"
          class="plan-limits-icon"
          preset="help"
          :text="helpTooltip"
        />
        <ui-plan-limits v-if="showPlanLimitsIcon" class="plan-limits-icon" />
      </div>
      <div class="ui-button__main">
        <slot name="body">
          <ui-icon v-if="icon" :color="contentColor" class="icon" :icon="icon" />
          <div class="content">
            <span
              v-if="text"
              :style="{ color: contentColor }"
              class="text"
              :class="{ 'ml-4': icon }"
              >{{ text }}</span
            >
          </div>

          <slot name="icon-append">
            <ui-icon v-if="iconAppend" class="icon ml-4" :icon="iconAppend" />
          </slot>

          <div v-if="forceChevron || hasMenu" class="menu-wrapper">
            <ui-chevron
              v-if="forceChevron || (hasMenu && text && !forceHideChevron)"
              class="chevron ml-4"
              :open="menuOpen"
              :disabled="disabled"
            />

            <!-- Anchor only — open state is toggled by the button click (same as before) -->
            <ui-menu
              v-if="hasMenu"
              :model-value="menuOpen"
              @update:model-value="setMenuOpen"
              :has-triangle="true"
              :menu-props="menuContentProps"
              :content-class="menuContentClass"
              :items="menuList"
              :selected="menuKeyOfSelectedItem"
              :location="menuLocation"
              :offset="menuOffset"
              :target="menuTarget"
              :location-strategy="menuLocationStrategy"
              :eager="menuEager"
              :attach="menuAttach"
              ref="menuRef"
            >
              <template #activator="{ props: activatorProps }">
                <span
                  v-bind="activatorProps"
                  class="ui-button__menu-anchor"
                  @click.stop
                />
              </template>
              <template v-if="slots.menu" #menu>
                <slot name="menu" />
              </template>
              <template v-if="slots.item" #item="{ item }">
                <slot name="item" :item="item" />
              </template>
            </ui-menu>
          </div>
        </slot>
      </div>
      <loading-circle v-if="loading" class="loader" :width="12" :height="12" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useSlots, watch } from 'vue';
import { useRouter } from 'vue-router';
import LoadingCircle from '@/components/loading-circle.vue';
import { usePlanLimits, useColors } from '@/composables';

const props = withDefaults(
  defineProps<{
    href?: string;
    icon?: string;
    helpTooltip?: string;
    menuKeyOfSelectedItem?: string;
    menuProps?: Record<string, unknown>;
    color?: string;
    to?: object;
    contentColor?: string;
    forceChevron?: boolean;
    forceHideChevron?: boolean;
    menuList?: Bemob.ListItem[];
    dontOpenMenu?: boolean;
    menuContentClass?: string;
    iconAppend?: string;
    disabled?: boolean;
    buttonStyle?: string;
    target?: string;
    text?: string;
    loading?: boolean;
    size?: null | 'mini' | 'micro' | 'large';
    isMenuOpen?: boolean;
    menuEager?: boolean;
    menuAttach?: boolean | string | Element;
    planLimits?: import('@/composables').PlanLimitsProp;
    planLimitsEditable?: boolean;
  }>(),
  {
    href: '',
    forceChevron: false,
    forceHideChevron: false,
    dontOpenMenu: false,
    disabled: false,
    buttonStyle: 'default',
    target: '_blank',
    text: '',
    isMenuOpen: false,
    planLimitsEditable: false,
  },
);

const emit = defineEmits<{
  click: [];
  'update:isMenuOpen': [value: boolean];
}>();

const slots = useSlots();
const router = useRouter();
const $colors = useColors();

const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  () => props.planLimitsEditable,
);

const menuOpen = ref(props.isMenuOpen);
const buttonWrapperRef = ref<HTMLElement | null>(null);

watch(
  () => props.isMenuOpen,
  value => {
    menuOpen.value = value;
  },
);

const hasMenu = computed(
  () => !!(props.menuList?.length || slots['menu'] || slots['item']),
);

const buttonClasses = computed(() => ({
  disabled: props.disabled,
  [props.buttonStyle]: true,
  [`size_${props.size}`]: props.size,
  'ui-button__plan-limits': showPlanLimitsIcon.value,
  'pointer-events-none': props.loading,
  'ui-button--loading': props.loading,
}));

const menuLocation = computed(() => {
  const location = props.menuProps?.location;
  return typeof location === 'string' ? location : 'bottom end';
});

const menuOffset = computed((): [number, number] => {
  const offset = props.menuProps?.offset;
  if (Array.isArray(offset) && offset.length === 2) {
    return [Number(offset[0]), Number(offset[1])];
  }
  const nudgeBottom = props.size === 'mini' ? 8 : 12;
  const nudgeRight = 10;
  // The triangle menu's top margin sits outside the positioned box.
  const triangleMargin = 5;
  return [nudgeBottom + triangleMargin, nudgeRight];
});

const menuTarget = computed(() => {
  if (props.menuProps?.target === 'wrapper') {
    return buttonWrapperRef.value;
  }
  return undefined;
});

const menuLocationStrategy = computed(() => props.menuProps?.locationStrategy);

const menuContentProps = computed(() => {
  if (!props.menuProps) return {};

  const contentProps = { ...props.menuProps };
  delete contentProps.location;
  delete contentProps.offset;
  delete contentProps.eager;
  delete contentProps.attach;
  delete contentProps.target;
  delete contentProps.locationStrategy;
  return contentProps;
});

const setMenuOpen = (value: boolean) => {
  menuOpen.value = value;
  emit('update:isMenuOpen', value);
};

const toggleMenu = (isOpen: boolean = !menuOpen.value) => {
  setMenuOpen(isOpen);
};

defineExpose({
  toggleMenu,
});

const onClick = () => {
  if (showPlanLimitsIcon.value) {
    handleClickPlanLimits();
    return;
  }
  emit('click');
  if (!props.dontOpenMenu && hasMenu.value) {
    const next = !menuOpen.value;
    if (next) {
      // Defer open so this click is not treated as an outside-close
      nextTick(() => setMenuOpen(true));
    } else {
      setMenuOpen(false);
    }
  }
  if (props.href) {
    window.open(props.href, props.target);
  }
  if (props.to) {
    router.push(props.to);
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/ui/button-like-default.scss';

.btn-wrapper {
  display: flex;
  align-items: center;

  & + & {
    margin-left: 8px;
  }
}

.ui-button__main {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
}

.menu-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.ui-button__menu-anchor {
  position: absolute;
  top: 50%;
  right: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.ui-button--loading {
  .top-icons,
  .ui-button__main {
    opacity: 0;
    pointer-events: none;
  }
}

.loader {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  background-color: inherit;
  background-image: inherit;
  color: inherit;
  border-radius: inherit;
  cursor: default;
  pointer-events: none;

  :deep(&.accent &.show) {
    .sk-fading-circle {
      .sk-circle:before {
        background-color: var(--color-white) !important;
      }
    }
  }
}

.ui-button {
  display: flex;
  align-items: center;
  position: relative;
  height: 32px;
  min-width: 32px;
  border-radius: 6px;
  padding: 0 8px;
  flex: 0 0 auto;

  .icon {
    line-height: 10px;
  }

  &.size_mini {
    height: 24px;
    border-radius: 4px;

    :deep(&.v-text-field input) {
      padding-top: 0;
      padding-bottom: 0;
    }
    .icon {
      font-size: 10px !important;
    }
  }

  &.size_micro {
    height: 18px;
    border-radius: 8px;
    .icon {
      font-size: 10px !important;
    }
  }

  &.size_large {
    height: 34px;
    .text {
      font-size: 13px !important;
    }
  }

  &:hover:before {
    background-color: currentColor !important;
  }

  .text {
    text-transform: none;
    font-size: 12px;
    line-height: 1.33;
    color: inherit;
    font-weight: 400;
  }

  &:not(.disabled):not(.simple):hover {
    &:before {
      border-radius: inherit;
      color: inherit;
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      opacity: 0.12;
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      width: 100%;
    }
  }

  &.disabled:not(.accent):not(.red):not(.blue) {
    :deep(.ui-icon) {
      color: var(--color-gray40) !important;
    }
  }
}

.menu-wrapper {
  display: flex;
  align-items: center;
}

.default {
  @include buttonLikeDefaultIdle();

  .text {
    color: var(--color-graphite);
  }

  &:active:before {
    background-color: transparent;
  }

  &.disabled {
    @include buttonLikeDefaultDisabled(true);

    .content,
    .chevron,
    .text,
    .icon {
      color: var(--color-gray40);
    }
  }
}

.plain {
  border: 1px solid currentColor;

  .text {
    color: var(--color-graphite);
  }

  &:active:before {
    background-color: transparent;
  }

  &.disabled {
    @include buttonLikeDefaultDisabled(true);

    .content,
    .chevron,
    .text,
    .icon {
      color: var(--color-gray40);
    }
  }
}

.red {
  border: solid 1px var(--color-red) !important;
  background-color: var(--color-red) !important;

  .text {
    color: var(--color-white);
  }

  .icon {
    color: var(--color-white) !important;
  }

  &:hover {
    &:before {
      background-color: var(--color-black10);
      opacity: 1;
    }
  }

  &:active,
  &:focus {
    border: solid 1px var(--color-red);

    &:before {
      background-color: var(--color-black10);
      opacity: 1;
    }
  }
}

.blue {
  border: solid 1px var(--color-blue) !important;
  background-color: var(--color-blue) !important;

  .text {
    color: var(--color-white);
  }

  .icon {
    color: var(--color-white) !important;
  }

  &:hover {
    &:before {
      background-color: var(--color-black10);
      opacity: 1;
    }
  }

  &:active,
  &:focus {
    border: solid 1px var(--color-blue);

    &:before {
      background-color: var(--color-black10);
      opacity: 1;
    }
  }

  &.disabled {
    border: solid 1px var(--color-blue50) !important;
    background-color: var(--color-blue50) !important;
  }
}

.accent {
  border: solid 1px var(--color-green) !important;
  background-color: var(--color-green) !important;

  .text,
  .icon,
  .chevron :deep(.ui-icon) {
    color: var(--color-white) !important;
  }

  &:hover:before {
    background-color: var(--color-black10);
    opacity: 1;
  }

  &:active,
  &:focus {
    border: solid 1px var(--color-green);

    &:before {
      background-color: var(--color-black10);
      opacity: 1;
    }
  }

  &.disabled {
    border: solid 1px var(--color-green50) !important;
    background-color: var(--color-green50) !important;
    background-clip: padding-box;
  }
}

.outline {
  background-color: var(--color-gray5) !important;

  &:hover:before {
    background-color: var(--color-gray50) !important;
  }
}

.contained {
  border: solid 1px var(--color-blue);
  background-image:
    linear-gradient(to bottom, var(--color-blue), var(--color-blue)),
    linear-gradient(to top, var(--color-black), var(--color-white));
  height: 40px;
  width: 100%;
  justify-content: center;

  .icon {
    color: var(--color-white) !important;
  }
  .text {
    color: var(--color-white);
  }

  &:hover:before {
    background-color: var(--color-black10);
    opacity: 1;
  }

  &:active,
  &:focus {
    border: solid 1px var(--color-gray20);

    &:before {
      background-color: var(--color-black10);
      opacity: 1;
    }
  }

  &.disabled {
    border: solid 1px var(--color-gray10);
    background-color: var(--color-gray10);
    background-image: none;
    background-clip: padding-box;

    .icon {
      color: var(--color-gray40) !important;
    }
    .text {
      color: var(--color-gray40);
    }
  }
}

.outline-alt {
  @include buttonLikeDefaultIdle();

  &:hover {
    @include buttonLikeDefaultHover();
  }

  &:active:not(.disabled),
  &:focus:not(.disabled) {
    @include buttonLikeDefaultFocus();
  }

  .text,
  .icon {
    color: var(--color-graphite);
  }

  &.disabled {
    .content,
    .text,
    .icon {
      color: var(--color-gray40);
    }
  }

  &:active,
  &:focus,
  &:hover,
  & {
    border: solid 1px var(--color-red);
  }
}

.outline-accent {
  @include buttonLikeDefaultIdle();

  &:hover {
    @include buttonLikeDefaultHover();
  }

  &:active,
  &:focus {
    @include buttonLikeDefaultFocus();
  }

  .text,
  .icon {
    color: var(--color-graphite);
  }

  &:active,
  &:focus,
  &:hover,
  & {
    border: solid 1px var(--color-green);
  }

  &.disabled {
    opacity: 0.2;
  }
}

.simple {
  min-width: initial;
  padding: initial;
  height: initial;
  border-radius: initial;
  .text,
  .icon {
    font-size: 13px;
  }
  &:disabled {
    .text,
    .icon {
      color: var(--color-gray40) !important;
    }
  }
  .plan-limits-icon {
    top: -6px;
    right: -9px;
    width: 10px;
  }
  &.ui-button__plan-limits {
    margin-right: 8px;
  }
}

.content {
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 1.33;
}

.plan-limits-icon {
}

.top-icons {
  line-height: 14px;
  position: absolute;
  top: -8px;
  right: -7px;
  z-index: 10;
  display: flex;
  align-items: center;
  column-gap: 4px;
}
</style>
